<template>
  <div class="ui-filter-param__field--name">
    <component
      :is="fieldInputComponent"
      v-model="field"
      :data="fieldsData"
      :container
      :size
      :placeholder="t('Select field')"
      :readonly="param.readonly"
      placement="bottom-end"
      @ready="handleFieldReady"
    />
  </div>
  <div class="ui-filter-param__action ui-filter-param__action--sm">
    <RemoveButton
      v-if="!param.readonly"
      :disabled="isRemoveButtonDisabled"
      @click="handleRemove"
    />
    <LockClosedIcon
      v-else
      class="ui-filter-param__action-icon"
    />
  </div>
  <div class="ui-filter-param__field--comparison">
    <UIInputSelect
      v-if="isComparisonVisible"
      v-model="comparison"
      :data="comparisonData"
      :container
      :size
      :placeholder="t('Select comparison')"
      placement="bottom-end"
      :readonly="param.readonly"
      @ready="handleComparisonReady"
    />
  </div>
  <div class="ui-filter-param__field--value">
    <GroupsItemParamValue
      v-if="isValueVisible"
      ref="valueRef"
      v-model="paramValue"
      :readonly="param.readonly"
      :comparison
      :field-settings
      :filter-context
    />
  </div>
  <div class="ui-filter-param__action ui-filter-param__action--md">
    <RemoveButton
      v-if="!param.readonly"
      :disabled="isRemoveButtonDisabled"
      @click="handleRemove"
    />
    <LockClosedIcon
      v-else
      class="ui-filter-param__action-icon"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref, useTemplateRef } from 'vue'

import { ButtonSize, I18nTranslate } from '@types'
import { Filter, FilterField, FilterParam } from '../utils/types'
import { FilterComparison } from '../utils/enums'
import { useResponsive } from '@/plugins/responsiveUI'

import {
  getComparisonData,
  getDefaultComparison,
  getDefaultValue,
} from './utils/helpers'

import GroupsItemParamValue from './GroupsItemParamValue.vue'
import RemoveButton from './RemoveButton.vue'
import { UIInputAutocomplete, UIInputSelect } from '@ui/fields'
import { LockClosedIcon } from '@heroicons/vue/20/solid'

type Props = {
  fields?: FilterField[]
  param: FilterParam
  groupIndex: number
  index: number
}

type Emits = {
  'remove:param': [key: string]
  'update:param': [key: string, data: Partial<FilterParam>]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  start() {
    fieldRef.value?.focus()
  },
})

const container = inject<string | undefined>('container', undefined)
const size = inject<ButtonSize>('size')
const t = inject<I18nTranslate>('t', (data: string) => data)
type GetFilterContext = (excludeKey?: string) => Filter | undefined
const getFilterContext = inject<GetFilterContext>('getFilterContext', () => undefined)
const { isMobile } = useResponsive()

const fieldRef = ref<HTMLInputElement>()
const comparisonRef = ref<HTMLInputElement>()
const valueRef = useTemplateRef('valueRef')

const fieldSettings = ref<FilterField>()

const isFilled = computed(
  () =>
    !!comparison.value &&
    (!!paramValue.value ||
      [FilterComparison.EMPTY, FilterComparison.NOTEMPTY].includes(
        comparison.value,
      )),
)

const isRemoveButtonDisabled = computed(
  () => props.groupIndex === 0 && props.index === 0 && !isFilled.value,
)

const fieldsData = computed(
  () =>
    props.fields?.map(field => ({ value: field.name, label: field.caption })) ||
    [],
)

const fieldInputComponent = computed(() =>
  isMobile.value ? UIInputSelect : UIInputAutocomplete,
)

const comparisonData = computed(() =>
  getComparisonData(fieldSettings.value).map(value => ({
    value,
    label: t(value),
  })),
)

const field = computed({
  get() {
    return props.param.field
  },
  set(field) {
    fieldSettings.value = props.fields?.find(item => item.name === field)
    const comparison = getDefaultComparison(fieldSettings.value)
    const value = getDefaultValue(comparison, fieldSettings.value)
    updateParam({ comparison, field, value })
    setTimeout(() => {
      comparisonRef.value?.focus()
      valueRef.value?.initData?.()
    })
  },
})

const comparison = computed({
  get() {
    return props.param.comparison
  },
  set(comparison) {
    fieldSettings.value = props.fields?.find(item => item.name === field.value)
    const value = getDefaultValue(
      comparison,
      fieldSettings.value,
      props.param.comparison,
      paramValue.value,
    )
    updateParam({ comparison, value })
    setTimeout(() => {
      valueRef.value?.start()
    })
  },
})

const paramValue = computed({
  get() {
    return props.param.value ? `${props.param.value}` : ''
  },
  set(value) {
    updateParam({ value })
  },
})

const isComparisonVisible = computed(() => !!field.value)
const isValueVisible = computed(() => !!comparison.value)

// narrow value options by other active params (excluding this one); dropdown applies context per comparison
const filterContext = computed(() => getFilterContext(props.param.key))

const handleFieldReady = (el: HTMLInputElement) => {
  fieldRef.value = el
}

const handleComparisonReady = (el: HTMLInputElement) => {
  comparisonRef.value = el
}

const updateParam = (data: Partial<FilterParam>) => {
  if (!props.param.key) return
  emit('update:param', props.param.key, data)
}

const handleRemove = () => {
  if (!props.param.key) return
  emit('remove:param', props.param.key)
}

onBeforeMount(() => {
  fieldSettings.value = props.fields?.find(
    item => item.name === props.param.field,
  )
})
</script>

<style scoped>
.ui-filter-param {
  &__field {
    &--comparison,
    &--value {
      @apply col-span-2 md:col-auto;

      & > * {
        @apply mt-2 md:mt-0;
      }
    }
  }
  &__action {
    @apply items-center;

    &--sm {
      @apply flex md:hidden;

      .ui-button-icon {
        @apply ml-1;
      }
    }

    &--md {
      @apply hidden md:flex;

      .ui-button-icon {
        @apply -mx-1;
      }
    }

    :deep(.ui-button__icon) {
      @apply size-4;
    }
  }

  &__action-icon {
    @apply size-4;
    @apply mx-[.315rem];
    @apply text-gray-200 dark:text-gray-600;
  }
}
</style>
